class GuidedContactForm extends HTMLElement {
  connectedCallback() {
    this.init();
  }

  init() {
    window.wetheme?.webcomponentRegistry?.register({ key: 'guided-contact-form' });

    this.form = this.querySelector('form');
    if (!this.form) return;

    this.panels = {
      topics: this.querySelector('[data-gcf-panel="1"]'),
      form: this.querySelector('[data-gcf-panel="2"]'),
    };
    // After a successful submit the form renders only the success message.
    if (!this.panels.topics || !this.panels.form) return;

    this.cards = Array.from(this.querySelectorAll('[data-gcf-card]'));
    this.backButton = this.querySelector('[data-gcf-back]');
    this.selectedTitle = this.querySelector('[data-gcf-selected-title]');
    this.inquiryTypeInput = this.querySelector('[data-gcf-inquiry-type]');
    this.messageLabel = this.querySelector('[data-gcf-message-label]');
    this.messageInput = this.querySelector('#gcf-message');
    this.helper = this.querySelector('[data-gcf-helper]');
    this.extraFields = Array.from(this.querySelectorAll('[data-field]'));
    this.orderInput = this.querySelector('#gcf-order');
    this.selectedCard = null;

    // Take over validation so messages stay friendly and consistent
    this.form.noValidate = true;

    this.cards.forEach((card) => {
      card.addEventListener('click', () => this.selectCategory(card));
    });
    this.backButton.addEventListener('click', () => this.showTopics());
    this.form.addEventListener('submit', (event) => this.handleSubmit(event));

    this.querySelectorAll('.gcf-input').forEach((input) => {
      input.addEventListener('input', () => this.clearFieldError(input));
    });

    // If the server bounced the submission back, reopen the form step
    // pre-filled instead of making the visitor start over.
    if (this.querySelector('[data-gcf-server-errors]')) {
      const previousType = this.readInquiryTypeFromBody();
      const card = this.cards.find((c) => c.dataset.title === previousType);
      this.selectCategory(card || this.cards[this.cards.length - 1], { animate: false, focus: false });
    } else {
      this.showTopics({ animate: false, focus: false });
    }
  }

  selectCategory(card, { animate = true, focus = true } = {}) {
    this.selectedCard = card;
    const category = card.dataset.category;
    const extras = card.dataset.extraFields
      ? card.dataset.extraFields.split(',').map((f) => f.trim()).filter(Boolean)
      : [];

    this.inquiryTypeInput.disabled = false;
    this.inquiryTypeInput.value = card.dataset.title;
    this.selectedTitle.textContent = card.dataset.title;

    const requiredMark = this.messageLabel.querySelector('.gcf-required');
    this.messageLabel.textContent = card.dataset.messageLabel + ' ';
    if (requiredMark) this.messageLabel.appendChild(requiredMark);
    this.messageInput.setAttribute('aria-label', card.dataset.messageLabel);

    this.extraFields.forEach((wrapper) => {
      const active = extras.includes(wrapper.dataset.field);
      wrapper.hidden = !active;
      wrapper.querySelectorAll('input, textarea').forEach((input) => {
        input.disabled = !active;
      });
    });

    if (this.orderInput) this.orderInput.required = category === 'order';
    if (this.helper) this.helper.hidden = category !== 'assessment-tools';

    this.showPanel(this.panels.form, this.panels.topics, animate);
    if (focus) this.selectedTitle.focus({ preventScroll: false });
  }

  showTopics({ animate = true, focus = false } = {}) {
    // Disable step-2 inputs so nothing half-filled submits from step 1.
    this.inquiryTypeInput.disabled = true;
    this.showPanel(this.panels.topics, this.panels.form, animate);
    const target = this.selectedCard || this.cards[0];
    if (focus || this.selectedCard) target.focus();
  }

  showPanel(showEl, hideEl, animate) {
    hideEl.hidden = true;
    showEl.hidden = false;
    if (!animate) return;
    showEl.classList.add('gcf-panel--entering');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => showEl.classList.remove('gcf-panel--entering'));
    });
  }

  handleSubmit(event) {
    if (!this.validate()) {
      event.preventDefault();
      return;
    }
    this.prependDetailsToBody();
  }

  validate() {
    let firstInvalid = null;

    this.form.querySelectorAll('.gcf-input').forEach((input) => {
      if (input.disabled || input.closest('[hidden]')) return;

      let message = '';
      const value = input.value.trim();

      if (input.required && !value) {
        message = input.dataset.errorMessage || 'This field is required.';
      } else if (input.type === 'email' && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        message = input.dataset.formatErrorMessage || "That email doesn't look quite right.";
      }

      if (message) {
        this.setFieldError(input, message);
        if (!firstInvalid) firstInvalid = input;
      } else {
        this.clearFieldError(input);
      }
    });

    if (firstInvalid) {
      firstInvalid.focus();
      return false;
    }
    return true;
  }

  setFieldError(input, message) {
    input.setAttribute('aria-invalid', 'true');
    const error = this.errorElementFor(input);
    if (error) {
      error.textContent = message;
      error.hidden = false;
    }
  }

  clearFieldError(input) {
    input.removeAttribute('aria-invalid');
    const error = this.errorElementFor(input);
    if (error) {
      error.textContent = '';
      error.hidden = true;
    }
  }

  errorElementFor(input) {
    const id = input.getAttribute('aria-describedby');
    return id ? this.querySelector('#' + id) : null;
  }

  // Shopify's contact notification email reliably includes contact[body],
  // but custom contact[...] fields depend on the notification template.
  // Prepend everything into the body so the store owner always sees it.
  prependDetailsToBody() {
    const message = this.messageInput.value.trim();
    if (message.startsWith('Inquiry type:')) return; // already built (double submit)

    const lines = ['Inquiry type: ' + this.inquiryTypeInput.value];
    const details = [
      ['#gcf-order', 'Order number'],
      ['#gcf-business', 'Business name'],
      ['#gcf-website', 'Website'],
      ['#gcf-employees', 'Number of employees / workstations'],
    ];
    details.forEach(([selector, label]) => {
      const input = this.querySelector(selector);
      if (input && !input.disabled && input.value.trim()) {
        lines.push(label + ': ' + input.value.trim());
      }
    });

    this.messageInput.value = lines.join('\n') + '\n\n' + message;
  }

  // After a failed server-side submit, form.body echoes the combined message.
  // Recover the inquiry type so we can reopen the right category.
  readInquiryTypeFromBody() {
    const match = this.messageInput.value.match(/^Inquiry type: (.+)$/m);
    return match ? match[1].trim() : null;
  }
}

customElements.define('guided-contact-form', GuidedContactForm);
